import styled from 'styled-components';

export const MessageTextStyle = styled.div`
  /* Notion-style variables */
  --font-size: 16px;
  --table-font-size: 14px;
  --notion-text: ${(props) => props.theme.colors.gray800};
  --notion-text-light: ${(props) => props.theme.colors.gray600};
  --notion-bg-gray: ${(props) => props.theme.colors.gray200};
  --notion-border: ${(props) => props.theme.colors.gray300};
  --notion-code-bg: rgba(135, 131, 120, 0.15);
  --notion-code-text: #eb5757;

  word-wrap: break-word;
  overflow-wrap: anywhere;
  word-break: break-word;
  max-width: 100%;
  min-width: 0;
  color: var(--notion-text);
  line-height: 1.5;
  font-size: var(--font-size);

  /* Notion-style spacing between blocks */
  > * + * {
    margin-top: 0.25em;
  }

  /* Smooth transitions like Notion */
  * {
    transition: background-color 20ms ease-in 0s;
  }

  /* Notion-style lists */
  ol,
  ul {
    margin: 4px 0;
    padding-left: 1.5em;
  }

  ol ol,
  ul ul {
    margin-top: 2px;
    margin-bottom: 2px;
  }

  ul {
    list-style: disc;
  }

  ol {
    list-style: decimal;
  }

  li {
    position: relative;
    margin: 2px 0;
    padding: 3px 0;
    line-height: 1.5;

    /* Notion-style list item hover effect */
    &::marker {
      color: var(--notion-text-light);
    }
  }

  li:last-child {
    margin-bottom: 0;
  }

  /* Nested list spacing */
  li > ol,
  li > ul {
    margin-top: 2px;
    margin-bottom: 2px;
  }

  /* Notion-style paragraph */
  p {
    margin: 0.25em 0;
    line-height: 1.5;
    white-space: pre-wrap;
  }

  /* Notion-style text formatting */
  strong,
  b {
    font-weight: 600;
    color: ${(props) => props.theme.colors.gray900};
  }

  em,
  i {
    font-style: italic;
  }

  u {
    text-decoration: underline;
  }

  s,
  del {
    text-decoration: line-through;
  }

  /* Notion-style inline code */
  code:not(pre code) {
    background: var(--notion-code-bg);
    color: var(--notion-code-text);
    border-radius: 3px;
    padding: 0.2em 0.4em;
    font-size: 85%;
    font-family:
      'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas,
      'Courier New', monospace;
    font-weight: 500;
  }

  /* Notion-style code block */
  pre {
    background: var(--notion-bg-gray);
    border-radius: 4px;
    padding: 16px;
    margin: 8px 0;
    overflow-x: auto;
    font-size: 85%;
    line-height: 1.5;

    code {
      background: none;
      color: inherit;
      padding: 0;
      font-family:
        'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas,
        'Courier New', monospace;
    }
  }

  /* Notion-style links */
  a {
    color: inherit;
    text-decoration: underline;
    text-decoration-color: ${(props) => props.theme.colors.gray400};
    text-underline-offset: 2px;
    transition: all 0.1s ease;

    &:hover {
      text-decoration-color: ${(props) => props.theme.colors.gray600};
    }

    &.citation {
      text-decoration: none;
      color: ${(props) => props.theme.colors.brand600};
      font-weight: 500;
      padding: 0 2px;

      &:hover {
        background: ${(props) => props.theme.colors.brand100};
        border-radius: 2px;
      }
    }
  }

  /* Notion-style blockquote / callout */
  blockquote {
    border-left: 3px solid var(--notion-border);
    padding: 8px 12px;
    margin: 8px 0;
    background: var(--notion-bg-gray);
    border-radius: 4px;
    color: var(--notion-text-light);

    p {
      margin: 0;
    }
  }

  /* Notion-style headings */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-bottom: 4px;
    padding: 3px 2px;
    font-weight: 600;
    color: ${(props) => props.theme.colors.gray900};
    line-height: 1.3;
    letter-spacing: -0.01em;
  }

  /* Notion H1 - Page title style */
  h1 {
    margin-top: calc(var(--font-size) * 2);
    font-size: 2.5em; /* 40px */
    line-height: 1.2;
    font-weight: 700;
    margin-bottom: 8px;
  }

  /* Notion H2 - Large heading */
  h2 {
    margin-top: calc(var(--font-size) * 2);
    font-size: 1.875em; /* 30px */
    font-weight: 700;
    margin-bottom: 6px;
  }

  /* Notion H3 - Medium heading */
  h3 {
    margin-top: calc(var(--font-size) * 1.3);
    font-size: 1.5em; /* 24px */
    font-weight: 600;
    margin-bottom: 4px;
  }

  /* Notion H4 - Small heading */
  h4 {
    margin-top: calc(var(--font-size) * 1);
    font-size: 1.125em; /* 18px */
    font-weight: 600;
    margin-bottom: 4px;
  }

  /* Notion H5 - Tiny heading */
  h5 {
    margin-top: calc(var(--font-size) * 0.8);
    font-size: 1em; /* 16px */
    font-weight: 600;
    margin-bottom: 4px;
  }

  /* Notion H6 - Smallest heading */
  h6 {
    margin-top: calc(var(--font-size) * 0.6);
    font-size: 0.875em; /* 14px */
    font-weight: 600;
    margin-bottom: 4px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--notion-text-light);
  }

  /* Notion-style table */
  table {
    border-collapse: collapse;
    display: block;
    font-size: var(--table-font-size);
    margin: 12px 0;
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
    border-radius: 4px;
  }

  thead {
    background: var(--notion-bg-gray);
  }

  th,
  td {
    padding: 8px 12px;
    text-align: left;
    border: 1px solid var(--notion-border);
  }

  th {
    background-color: var(--notion-bg-gray);
    font-weight: 600;
    color: ${(props) => props.theme.colors.gray900};
    min-width: 120px;
  }

  td {
    background-color: ${(props) => props.theme.colors.gray100};
  }

  /* Notion-style table row hover */
  tbody tr {
    transition: background-color 0.1s ease;

    &:hover {
      background-color: rgba(0, 0, 0, 0.02);
    }
  }

  /* Notion-style horizontal rule */
  hr {
    margin: 12px 0;
    border: none;
    border-top: 1px solid var(--notion-border);
  }

  /* Notion-style divider */
  hr::before {
    content: '';
    display: block;
    width: 100%;
  }

  code {
    background-color: ${(props) => props.theme.colors.gray200};
    padding: 2px 6px;
    border-radius: 4px;
  }

  img,
  svg,
  video,
  iframe {
    max-width: 100%;
  }
`;
